import { defineNuxtPlugin } from '#app'
import { Network } from '@shared/types'
import { defineRule } from 'vee-validate'
import { NUMBER_REGEX } from '@shared/utils/constant'
import { toBigNumber } from '@injectivelabs/utils/big-number'
import { defineTradeRules } from '@/app/client/utils/validation/trade'
import {
  UI_DEFAULT_MIN_DISPLAY_DECIMALS
} from '@/app/utils/constants'

const formatFieldName = (value: string) => {
  // Insert a space before all found uppercase letters in the string and trim the resulting string
  let result = value.replace(/([A-Z])/g, ' $1').trim()

  // Capitalize the first letter and join it with the rest of the string
  result = result.charAt(0).toUpperCase() + result.slice(1)

  return result
}

export const errorMessages = {
  [Network.Axelar]: () => 'This field is not a valid Cosmos address',
  [Network.CosmosHub]: () => 'This field is not a valid Cosmos address',
  [Network.Ethereum]: () => 'This field is not a valid Ethereum address',
  [Network.Evmos]: () => 'This field is not a valid Evmos address',
  [Network.Injective]: () => 'This field is not a valid Injective address',
  [Network.Osmosis]: () => 'This field is not a valid Osmosis address',
  [Network.Persistence]: () => 'This field is not a valid Persistence address',
  [Network.Secret]: () => 'This field is not a valid Secret Network address',
  [Network.Noble]: () => 'This field is not a valid Noble Network address',
  [Network.Stride]: () => 'This field is not a valid Stride address',
  [Network.Crescent]: () => 'This field is not a valid Crescent address',
  [Network.Sommelier]: () => 'This field is not a valid Sommelier address',
  [Network.Canto]: () => 'This field is not a valid Canto address',
  [Network.Kava]: () => 'This field is not a valid Kava address',
  [Network.Oraichain]: () => 'This field is not a valid Oraichain address',
  [Network.Migaloo]: () => 'This field is not a valid Migaloo address',
  [Network.Celestia]: () => 'This field is not a valid Celestia address'
} as Record<string, () => string>

export const defineGlobalRules = () => {
  defineRule('email', (value: string) => {
    const validEmailPattern =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

    if (!validEmailPattern.test(value)) {
      return 'This field should be a valid email'
    }

    return true
  })

  defineRule('between', (value: string, [min, max]: string[]) => {
    if (!min || !max) {
      return true
    }

    const valueIsOutOfBounds =
      Number(min) > Number(value) || Number(max) < Number(value)

    if (valueIsOutOfBounds) {
      return `${
        max <= min
          ? `Your input value of ${value} cannot be higher than ${max}`
          : `This field should be between ${min} and ${max}`
      }`
    }

    return true
  })

  defineRule('minValue', (value: string, [min]: string[]) => {
    if (!value || !min || Number(value) > Number(min)) {
      return true
    }

    return `This field should be greater than ${toBigNumber(min).toFormat()}`
  })

  defineRule('minValueInclusive', (value: string, [min]: string[]) => {
    if (!value || !min || toBigNumber(value).gte(toBigNumber(min))) {
      return true
    }

    return `This field should be greater than or equal to ${toBigNumber(min).toFormat()}`
  })

  defineRule('maxValue', (value: string, [max]: string[]) => {
    if (!value || !max || Number(value) < Number(max)) {
      return true
    }

    return `This field should be less than ${toBigNumber(max).toFormat()}`
  })

  defineRule('insufficientBalance', (value: string, [balance]: string[]) => {
    if (!value || !balance || Number(value) <= Number(balance)) {
      return true
    }

    return 'Insufficient balance'
  })

  defineRule(
    'liquidationValue',
    (value: string, [liquidationValue, isBuy]: string[]) => {
      const isBuyOrder = isBuy === 'true'

      if (
        !value ||
        (isBuyOrder && Number(value) > Number(liquidationValue)) ||
        (!isBuyOrder && Number(value) < Number(liquidationValue))
      ) {
        return true
      }

      if (isBuyOrder && Number(value) < Number(liquidationValue)) {
        return 'Should be higher than liquidation price'
      }

      return 'Should be lower than liquidation price'
    }
  )

  defineRule(
    'required',
    (value: string | number, _, { field }: { field: string }) => {
      if (!value || !value.toString().length || Number(value) === 0) {
        if (field.toLowerCase().includes('amount')) {
          return 'amount is required'
        }

        if (field.toLowerCase().includes('leverage')) {
          return 'Leverage is required'
        }

        if (field.toLowerCase().includes('positionquantity')) {
          return 'Invalid amount: Please enter a valid quantity greater than zero.'
        }

        if (field.includes('-')) {
          return `${field.replaceAll('-', ' ')} is required.`
        }

        return `${formatFieldName(field)} is required.`
      }

      return true
    }
  )

  defineRule('minCharacter', (value: string, [min]: number[]) => {
    if (min !== undefined && value.length < min) {
      return `Must be at least ${min} characters`
    }

    return true
  })

  defineRule('maxCharacter', (value: string, [max]: number[]) => {
    if (max !== undefined && value.length > max) {
      return 'Exceeds max characters'
    }

    return true
  })

  defineRule('injAddress', async (value: string) => {
    try {
      const { getEthereumAddress } = await import(
        '@injectivelabs/sdk-ts/utils/address'
      )

      getEthereumAddress(value)

      return true
    } catch {
      return 'This field is not a valid Injective address'
    }
  })

  defineRule('addressByNetwork', (value: string, [network]: Network[]) => {
    if (!network) {
      return true
    }

    if (network === Network.Ethereum) {
      if (!value.startsWith('0x')) {
        return errorMessages[network]?.() || 'Invalid address'
      }
    } else {
      const isValidCosmosAddress =
        network.toLowerCase().startsWith(value.toLowerCase().substring(0, 3)) &&
        toBigNumber(value.length).gte(3)

      if (!isValidCosmosAddress && errorMessages[network]) {
        return errorMessages[network]?.() || 'Invalid address'
      }
    }

    return true
  })

  defineRule('positiveNumber', (value: string) => {
    if (NUMBER_REGEX.test(value)) {
      return true
    }

    return 'This field is not a valid number'
  })

  defineRule('regex', (value: string, params: [string | RegExp]): boolean => {
    let regex = params[0]

    if (typeof regex === 'string') {
      regex = new RegExp(regex)
    }

    return regex.exec(value) !== null
  })

  defineRule('integer', (value: string, [fieldName]: string[]) => {
    const valueInBigNumber = toBigNumber(value || 0)

    if (valueInBigNumber.lte(0)) {
      return `${fieldName || 'Value'} must be > 0`
    }

    return true
  })

  defineRule('invalidIfBetween', (value: string, [min, max]: string[]) => {
    if (!min || !max) {
      return true
    }

    const valueInBigNumber = toBigNumber(value)
    const minInBigNumber = toBigNumber(min)
    const maxInBigNumber = toBigNumber(max)

    const isBetween =
      valueInBigNumber.lte(maxInBigNumber) &&
      valueInBigNumber.gte(minInBigNumber)

    if (isBetween) {
      return `Price range must be outside of ${minInBigNumber.toFixed(
        UI_DEFAULT_MIN_DISPLAY_DECIMALS
      )} - ${maxInBigNumber.toFixed(UI_DEFAULT_MIN_DISPLAY_DECIMALS)}`
    }

    return true
  })

  defineRule('requiredIfEmpty', (value: string, [fieldValue]: string[]) => {
    if (!fieldValue && !value) {
      return 'At least one field is required'
    }

    return true
  })

  defineRule('fixedCharacters', (value: string, [length]: string[]) => {
    if (value.length !== Number(length)) {
      return `This field must be exactly ${length || '0'} characters long.`
    }

    return true
  })

  defineRule('alphanumeric', (value: string) => {
    if (!/^[a-zA-Z0-9]+$/.test(value)) {
      return 'Only letters and numbers are allowed'
    }

    return true
  })

  defineRule('nonZeroPositionQuantity', (value: string | number) => {
    if (toBigNumber(value).isZero()) {
      return 'Invalid amount: Please enter a valid quantity greater than zero.'
    }

    return true
  })

  defineRule(
    'maxValuePositionQuantity',
    (value: string, [max]: string[], { field }: { field: string }) => {
      if (!max) {
        return true
      }

      if (toBigNumber(max).lt(value)) {
        if (field.toLowerCase() === 'positionquantity') {
          return 'Invalid amount: You cannot set an amount for more than your total position size.'
        }

        if (field.toLowerCase() === 'tppositionquantity') {
          return 'Invalid amount: You cannot set a TP for more than your total position size.'
        }

        if (field.toLowerCase() === 'slpositionquantity') {
          return 'Invalid amount: You cannot set a SL for more than your total position size.'
        }
      }

      return true
    }
  )

  defineRule('validMemoValue', (value: string) => {
    if (!/^[a-z0-9 _-]+$/i.test(value)) {
      return 'Invalid memo value'
    }

    return true
  })
}

export default defineNuxtPlugin(() => {
  defineGlobalRules()
  defineTradeRules()
})
